import React, { useMemo, useRef } from 'react';
import type { Chart as ChartJS, ChartOptions } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import type { Context as DataLabelContext } from 'chartjs-plugin-datalabels';
import type { AzureMonthlyTotal } from '../../../types/azureCost';

function formatAxisCost(value: number): string {
  if (value >= 1000) {
    return `${Math.round(value / 1000)}K`;
  }
  return String(Math.round(value));
}

function formatBarLabel(value: number): string {
  return Math.round(value).toLocaleString('en-US');
}

export interface AzureCost3MonthBarChartProps {
  months: AzureMonthlyTotal[];
  currency: string;
  className?: string;
}

const AzureCost3MonthBarChart: React.FC<AzureCost3MonthBarChartProps> = ({
  months,
  currency,
  className = '',
}) => {
  const chartRef = useRef<ChartJS<'bar'>>(null);
  const labels = useMemo(() => months.map((m) => m.label), [months]);
  const values = useMemo(() => months.map((m) => m.totalCost), [months]);
  const hasCostData = useMemo(() => values.some((v) => v > 0), [values]);

  const suggestedMax = useMemo(() => {
    const max = Math.max(0, ...values);
    return max > 0 ? max * 1.15 : 100;
  }, [values]);

  const options: ChartOptions<'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 28, right: 8, left: 4, bottom: 4 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const raw = Number(ctx.raw);
              return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency,
                minimumFractionDigits: 2,
              }).format(raw);
            },
          },
        },
        datalabels: { display: false },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: '#64748b', font: { size: 12, weight: 500 } },
        },
        y: {
          beginAtZero: true,
          suggestedMax,
          border: { display: false },
          grid: { color: 'rgba(148, 163, 184, 0.25)' },
          ticks: {
            color: '#94a3b8',
            maxTicksLimit: 6,
            callback: (v) => formatAxisCost(Number(v)),
          },
        },
      },
    }),
    [currency, suggestedMax]
  );

  if (!months.length || !hasCostData) {
    return (
      <div
        className={`flex min-h-[300px] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50/50 text-sm text-slate-500 ${className}`}
      >
        No monthly cost data available.
      </div>
    );
  }

  return (
    <div className={`relative min-h-[300px] w-full ${className}`}>
      <Bar
        ref={chartRef}
        options={options}
        data={{
          labels,
          datasets: [
            {
              label: 'Monthly cost',
              data: values,
              backgroundColor: '#3465a4',
              borderRadius: 4,
              barThickness: 56,
              datalabels: {
                display: true,
                anchor: 'end',
                align: 'end',
                offset: 4,
                color: '#1e293b',
                font: { weight: 600, size: 11 },
                formatter: (value: number, ctx: DataLabelContext) => {
                  const v = Number(ctx.dataset.data[ctx.dataIndex]);
                  return Number.isFinite(v) ? formatBarLabel(v) : '';
                },
              },
            },
          ],
        }}
      />
    </div>
  );
};

export default AzureCost3MonthBarChart;
